'use client';

import React from 'react';
import { Shield, X, MessageCircle, AlertTriangle, Lock, Users } from 'lucide-react';

const rules = [
  {
    icon: Users,
    title: 'No harassment or hate speech',
    body: 'Treat every stranger with basic respect. Threats, slurs, and targeted abuse are not tolerated.',
  },
  {
    icon: AlertTriangle,
    title: 'No sexual or explicit content',
    body: 'Keep it clean. Sexual messages, solicitations, and explicit content result in an immediate ban.',
  },
  {
    icon: Lock,
    title: 'No sharing personal information',
    body: "Never share your (or others') full name, address, phone number, or social media handles.",
  },
  {
    icon: MessageCircle,
    title: 'No spam or flooding',
    body: 'Repeated identical messages or rapid-fire sends will get you muted automatically.',
  },
];

interface RulesModalProps {
  onAccept: () => void;
}

export function RulesModal({ onAccept }: RulesModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm px-4">
      <div className="bg-brand-surface border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="px-6 pt-6 pb-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-brand-lime/10 border border-brand-lime/20 flex items-center justify-center shrink-0">
              <Shield size={18} className="text-brand-lime" />
            </div>
            <h2 className="text-base font-semibold text-brand-white">Community Rules</h2>
          </div>
          <p className="text-sm text-brand-gray leading-relaxed mt-3">
            Stranger World is built on mutual respect. Please read and agree to these rules before starting your first chat.
          </p>
        </div>

        {/* Rules list */}
        <div className="px-6 py-4 space-y-3">
          {rules.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-brand-surface-2 border border-white/[0.06] flex items-center justify-center shrink-0 mt-0.5">
                <Icon size={14} className="text-brand-lime" />
              </div>
              <div>
                <p className="text-sm font-medium text-brand-white leading-snug">{title}</p>
                <p className="text-xs text-brand-gray leading-relaxed mt-0.5">{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2">
          <p className="text-xs text-brand-gray text-center mb-4">
            Violations may result in muting, cooldowns, or permanent bans.
          </p>
          <button
            onClick={onAccept}
            className="w-full h-11 rounded-xl bg-brand-lime text-brand-black text-sm font-semibold hover:bg-brand-lime/90 active:scale-[0.98] transition-all"
          >
            I Agree &amp; Start Chatting
          </button>
        </div>
      </div>
    </div>
  );
}
